// src/hooks/useStorageInfo.ts
import { useState, useEffect } from 'react';
import { fetchStorageUsage } from '@/services/assetManagerService';
import { usePageContext } from '@/contexts/PageContext';

export function useStorageInfo() {
  // Initialize storageInfo with used and total in MB
  const [storageInfo, setStorageInfo] = useState<{ used: number; total: number }>({ used: 0, total: 0 });
  const { subscription } = usePageContext();

  // Convert total storage from GB to MB
  const totalStorageGB = subscription?.totalAssetSpaceGB || 0;
  const totalStorageMB = totalStorageGB * 1024; // 1 GB = 1024 MB

  const refreshStorageInfo = async () => {
    try {
      const response = await fetchStorageUsage();
      console.log('Fetched storage usage:', response); // Debugging

      // Ensure response.used is in MB
      setStorageInfo({
        used: response.used, // Already in MB
        total: totalStorageMB, // Converted to MB
      });
    } catch (error) {
      console.error('Error fetching storage info:', error);
    }
  };

  useEffect(() => {
    refreshStorageInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalStorageMB]); // Re-fetch if total storage changes

  // Destructure used and total from storageInfo
  const { used, total } = storageInfo;

  // Calculate used in MB and GB
  const usedMB = used.toFixed(2);
  const usedGB = (used / 1024).toFixed(2);

  // Calculate used percentage based on MB
  const usedPercentage = total > 0 ? Math.min(100, (used / total) * 100).toFixed(2) : '0.00';

  // Format used storage for display
  const formattedUsed = Number(usedGB) >= 1 ? `${usedGB} GB` : `${usedMB} MB`;

  return {
    ...storageInfo,
    refreshStorageInfo,
    usedMB,
    usedGB,
    formattedUsed,
    usedPercentage,
  };
}

